import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import {
  Build,
  Schedule,
  Warning,
  CheckCircle,
  Error as ErrorIcon,
  Add,
  Edit,
  ExpandMore,
  Timeline,
  PrecisionManufacturing,
  EngineeringOutlined,
  CalendarToday,
  TrendingUp,
  TrendingDown,
  Notifications,
  Settings,
  Refresh,
  Close,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import {
  fetchEquipment,
  fetchMaintenanceTasks,
  fetchMaintenanceAlerts,
  acknowledgeAlert,
  updateTaskStatus,
  clearError,
  createMaintenanceTask,
  fetchFailurePredictions,
} from '../../store/slices/maintenanceSlice';
import type { CreateMaintenanceTaskDto, FailurePrediction, UpdateTaskStatusPayload } from '../../store/slices/maintenanceSlice';

interface Equipment {
  id: string;
  code: string;
  name: string;
  type: string;
  location: string;
  status: 'operational' | 'maintenance' | 'breakdown' | 'idle';
  healthScore: number;
  lastMaintenance: string;
  nextMaintenance: string;
  utilizationRate: number;
  mtbf: number; // Mean Time Between Failures (hours)
  mttr: number; // Mean Time To Repair (hours)
}

interface MaintenanceTask {
  id: string;
  taskNumber: string;
  equipmentId: string;
  equipmentName: string;
  type: 'preventive' | 'corrective' | 'predictive' | 'emergency';
  priority: 'low' | 'medium' | 'high' | 'critical';
  status: 'scheduled' | 'in_progress' | 'completed' | 'overdue' | 'cancelled';
  description: string;
  assignedTo?: string;
  scheduledDate: string;
  completedDate?: string;
  estimatedDuration: number; // hours
  actualDuration?: number; // hours
  cost?: number;
  notes?: string;
}

interface MaintenanceAlert {
  id: string;
  equipmentId: string;
  equipmentName: string;
  alertType: 'overdue_maintenance' | 'health_degradation' | 'failure_prediction' | 'anomaly_detected';
  severity: 'info' | 'warning' | 'error' | 'critical';
  message: string;
  timestamp: string;
  acknowledged: boolean;
}

const PIE_COLORS = ['#4caf50', '#ff9800', '#2196f3', '#f44336'];

const Maintenance: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { equipment, maintenanceTasks, alerts, predictions, isLoading, isCreating, error } = useSelector(
    (state: RootState) => state.maintenance
  );

  const [selectedTab, setSelectedTab] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment | null>(null);

  // Completion dialog state
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
  const [completionTargetId, setCompletionTargetId] = useState<string>('');
  const [completionNotes, setCompletionNotes] = useState<string>('');
  const [actualDuration, setActualDuration] = useState<number | ''>('');
  const [completionNotesError, setCompletionNotesError] = useState<string>('');
  const [actualDurationError, setActualDurationError] = useState<string>('');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // Due This Week banner state
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const tasksTabRef = useRef<HTMLDivElement>(null);

  // Schedule Maintenance dialog state
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleForm, setScheduleForm] = useState<CreateMaintenanceTaskDto>({
    equipmentId: '',
    type: 'preventive',
    priority: 'medium',
    scheduledDate: '',
    estimatedDuration: 1,
    assignedTo: '',
    description: '',
  });
  const [scheduleError, setScheduleError] = useState<string | null>(null);

  // Load all maintenance data on mount
  useEffect(() => {
    dispatch(fetchEquipment());
    dispatch(fetchMaintenanceTasks());
    dispatch(fetchMaintenanceAlerts());
  }, [dispatch]);

  const handleRefresh = () => {
    dispatch(clearError());
    dispatch(fetchEquipment());
    dispatch(fetchMaintenanceTasks());
    dispatch(fetchMaintenanceAlerts());
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational':
      case 'completed':
        return 'success';
      case 'maintenance':
      case 'scheduled':
      case 'in_progress':
        return 'warning';
      case 'breakdown':
      case 'overdue':
      case 'critical':
        return 'error';
      case 'idle':
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: MaintenanceTask['priority']) => {
    switch (priority) {
      case 'low':
        return 'success';
      case 'medium':
        return 'info';
      case 'high':
        return 'warning';
      case 'critical':
        return 'error';
      default:
        return 'default';
    }
  };

  const getSeverityColor = (severity: MaintenanceAlert['severity']) => {
    switch (severity) {
      case 'info':
        return 'info';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      case 'critical':
        return 'error';
      default:
        return 'default';
    }
  };

  const getHealthColor = (score: number) => {
    if (score >= 80) return 'success.main';
    if (score >= 60) return 'warning.main';
    return 'error.main';
  };

  const handleViewEquipmentDetails = (equip: Equipment) => {
    setSelectedEquipment(equip);
    setDialogOpen(true);
  };

  const handleAcknowledgeAlert = (alertId: string) => {
    dispatch(acknowledgeAlert(alertId));
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    if (newValue === 4) {
      dispatch(fetchFailurePredictions());
    }
  };

  // Due This Week computation
  const now = new Date();
  const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const dueThisWeek = (maintenanceTasks as MaintenanceTask[]).filter(
    (t) =>
      t.scheduledDate &&
      new Date(t.scheduledDate) <= nextWeek &&
      t.status !== 'completed' &&
      t.status !== 'cancelled'
  );

  const handleBannerTasksClick = () => {
    setSelectedTab(1);
    setTimeout(() => {
      tasksTabRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleStartTask = async (taskId: string) => {
    setIsUpdatingStatus(true);
    const result = await dispatch(updateTaskStatus({ id: taskId, status: 'in_progress' }));
    setIsUpdatingStatus(false);
    if (updateTaskStatus.fulfilled.match(result)) {
      dispatch(fetchMaintenanceTasks());
      showSnackbar('Task status updated', 'success');
    } else {
      showSnackbar((result.error?.message) || 'Failed to update task status', 'error');
    }
  };

  const handleOpenCompletionDialog = (taskId: string) => {
    setCompletionTargetId(taskId);
    setCompletionNotes('');
    setActualDuration('');
    setCompletionNotesError('');
    setActualDurationError('');
    setCompletionDialogOpen(true);
  };

  const handleCompleteTask = async () => {
    let valid = true;
    if (completionNotes.trim().length < 10) {
      setCompletionNotesError('Notes must be at least 10 characters');
      valid = false;
    } else {
      setCompletionNotesError('');
    }
    if (actualDuration === '' || Number(actualDuration) <= 0) {
      setActualDurationError('Enter a valid duration');
      valid = false;
    } else {
      setActualDurationError('');
    }
    if (!valid) return;

    let completedBy: string | undefined;
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        completedBy = payload.sub || payload.userId || payload.id;
      }
    } catch {
      completedBy = undefined;
    }

    setIsUpdatingStatus(true);
    const updatePayload: UpdateTaskStatusPayload = {
      id: completionTargetId,
      status: 'completed',
      completionNotes: completionNotes.trim(),
      actualDuration: Number(actualDuration),
      completedBy,
    };
    const result = await dispatch(updateTaskStatus(updatePayload));
    setIsUpdatingStatus(false);
    if (updateTaskStatus.fulfilled.match(result)) {
      setCompletionDialogOpen(false);
      dispatch(fetchMaintenanceTasks());
      showSnackbar('Task status updated', 'success');
    } else {
      showSnackbar((result.error?.message) || 'Failed to update task status', 'error');
    }
  };

  const handleScheduleFormChange = (field: keyof CreateMaintenanceTaskDto, value: string | number) => {
    setScheduleForm(prev => ({ ...prev, [field]: value }));
  };

  const handleScheduleSubmit = async () => {
    setScheduleError(null);
    if (!scheduleForm.equipmentId || !scheduleForm.scheduledDate) {
      setScheduleError('Equipment and Scheduled Date are required.');
      return;
    }
    const result = await dispatch(createMaintenanceTask(scheduleForm));
    if (createMaintenanceTask.fulfilled.match(result)) {
      dispatch(fetchMaintenanceTasks());
      setScheduleDialogOpen(false);
      setScheduleForm({
        equipmentId: '',
        type: 'preventive',
        priority: 'medium',
        scheduledDate: '',
        estimatedDuration: 1,
        assignedTo: '',
        description: '',
      });
    } else {
      setScheduleError((result.payload as string) || 'Failed to create task. Please try again.');
    }
  };

  const getPredictionBarColor = (probability: number) => {
    if (probability > 70) return 'error';
    if (probability > 40) return 'warning';
    return 'success';
  };

  // Derived KPIs from real data
  const operationalEquipment = equipment.filter(eq => eq.status === 'operational').length;
  const maintenanceEquipment = equipment.filter(eq => eq.status === 'maintenance').length;
  const breakdownEquipment = equipment.filter(eq => eq.status === 'breakdown').length;
  const totalEquipment = equipment.length;

  const activeTasks = maintenanceTasks.filter(task => task.status === 'in_progress').length;
  const overdueTasks = maintenanceTasks.filter(task => task.status === 'overdue').length;
  const scheduledTasks = maintenanceTasks.filter(task => task.status === 'scheduled').length;

  const unacknowledgedAlerts = alerts.filter(alert => !alert.acknowledged).length;
  const avgHealthScore = totalEquipment > 0
    ? equipment.reduce((sum, eq) => sum + eq.healthScore, 0) / totalEquipment
    : 0;

  // Build health trend data from live equipment (each piece as its own data point)
  const healthTrendData = equipment.map(eq => ({
    name: eq.code || eq.name,
    health: eq.healthScore,
    utilization: eq.utilizationRate,
  }));

  // Build maintenance type distribution from real tasks
  const taskTypeCounts = maintenanceTasks.reduce((acc: Record<string, number>, task) => {
    const key = task.type || 'other';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const maintenanceTypeData = Object.entries(taskTypeCounts).map(([name, value], i) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value,
    color: PIE_COLORS[i % PIE_COLORS.length],
  }));

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Maintenance Management
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={isLoading ? <CircularProgress size={16} /> : <Refresh />}
            onClick={handleRefresh}
            disabled={isLoading}
          >
            {isLoading ? 'Loading...' : 'Refresh'}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setScheduleDialogOpen(true)}
          >
            Schedule Maintenance
          </Button>
        </Box>
      </Box>

      {/* Error banner */}
      {error && (
        <Alert severity="warning" onClose={() => dispatch(clearError())} sx={{ mb: 2 }}>
          {error} — Could not load maintenance data from API.
        </Alert>
      )}

      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Operational Equipment
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {isLoading ? <CircularProgress size={28} /> : `${operationalEquipment}/${totalEquipment}`}
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Active Tasks
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {isLoading ? <CircularProgress size={28} /> : activeTasks}
                  </Typography>
                </Box>
                <Build sx={{ fontSize: 40, color: 'warning.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Health Score
                  </Typography>
                  <Typography variant="h4" color={getHealthColor(avgHealthScore)}>
                    {isLoading ? <CircularProgress size={28} /> : `${avgHealthScore.toFixed(0)}%`}
                  </Typography>
                </Box>
                <Timeline sx={{ fontSize: 40, color: getHealthColor(avgHealthScore) }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Critical Alerts
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {isLoading ? <CircularProgress size={28} /> : unacknowledgedAlerts}
                  </Typography>
                </Box>
                <Warning sx={{ fontSize: 40, color: 'error.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Due This Week Banner */}
      {dueThisWeek.length > 0 && !bannerDismissed && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <IconButton
              size="small"
              onClick={() => setBannerDismissed(true)}
              aria-label="dismiss"
            >
              <Close fontSize="small" />
            </IconButton>
          }
          onClick={handleBannerTasksClick}
          style={{ cursor: 'pointer' }}
        >
          <Typography variant="body2" fontWeight="medium">
            {dueThisWeek.length} maintenance task{dueThisWeek.length > 1 ? 's' : ''} due within the next 7 days
          </Typography>
          <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2 }}>
            {dueThisWeek.slice(0, 3).map((t) => (
              <li key={t.id}>
                <Typography variant="caption">
                  {t.equipmentName || (t as any).title || 'Unknown'} — due {t.scheduledDate ? new Date(t.scheduledDate).toLocaleDateString() : '--'}
                </Typography>
              </li>
            ))}
            {dueThisWeek.length > 3 && (
              <li>
                <Typography variant="caption">+ {dueThisWeek.length - 3} more</Typography>
              </li>
            )}
          </Box>
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Equipment Status" />
          <Tab label="Maintenance Tasks" />
          <Tab label="Alerts & Notifications" />
          <Tab label="Analytics" />
          <Tab label="Predictions" />
        </Tabs>
      </Paper>

      {/* Equipment Status Tab */}
      {selectedTab === 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Equipment Status
          </Typography>
          {equipment.length === 0 && !isLoading ? (
            <Alert severity="info">No equipment data found. The maintenance service may not be running.</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Equipment</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Health Score</TableCell>
                    <TableCell>Utilization</TableCell>
                    <TableCell>Last Maintenance</TableCell>
                    <TableCell>Next Maintenance</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(equipment as Equipment[]).map((equip) => (
                    <TableRow key={equip.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {equip.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {equip.code} - {equip.type}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{equip.location}</TableCell>
                      <TableCell>
                        <Chip
                          label={equip.status.replace('_', ' ').toUpperCase()}
                          color={getStatusColor(equip.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <LinearProgress
                            variant="determinate"
                            value={equip.healthScore}
                            sx={{
                              width: 60,
                              mr: 1,
                              height: 6,
                              borderRadius: 3,
                              backgroundColor: 'grey.300',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: getHealthColor(equip.healthScore)
                              }
                            }}
                          />
                          <Typography variant="body2" color={getHealthColor(equip.healthScore)}>
                            {equip.healthScore}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {equip.utilizationRate}%
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {equip.lastMaintenance ? new Date(equip.lastMaintenance).toLocaleDateString() : '--'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {equip.nextMaintenance ? new Date(equip.nextMaintenance).toLocaleDateString() : '--'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleViewEquipmentDetails(equip)}
                        >
                          <Settings />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Maintenance Tasks Tab */}
      {selectedTab === 1 && (
        <Box ref={tasksTabRef}>
          <Typography variant="h6" gutterBottom>
            Maintenance Tasks
          </Typography>
          {maintenanceTasks.length === 0 && !isLoading ? (
            <Alert severity="info">No maintenance tasks found.</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Task Number</TableCell>
                    <TableCell>Equipment</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Assigned To</TableCell>
                    <TableCell>Scheduled Date</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Cost</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(maintenanceTasks as MaintenanceTask[]).map((task) => (
                    <TableRow key={task.id}>
                      <TableCell>{task.taskNumber}</TableCell>
                      <TableCell>{task.equipmentName}</TableCell>
                      <TableCell>
                        <Chip
                          label={task.type.toUpperCase()}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={task.priority.toUpperCase()}
                          color={getPriorityColor(task.priority) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={task.status.replace('_', ' ').toUpperCase()}
                          color={getStatusColor(task.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{task.assignedTo || 'Unassigned'}</TableCell>
                      <TableCell>
                        {task.scheduledDate ? new Date(task.scheduledDate).toLocaleDateString() : '--'}
                      </TableCell>
                      <TableCell>{task.estimatedDuration}h</TableCell>
                      <TableCell>${task.cost || 0}</TableCell>
                      <TableCell>
                        {(task.status === 'scheduled' || (task.status as string) === 'pending') && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            disabled={isUpdatingStatus}
                            onClick={() => handleStartTask(task.id)}
                          >
                            Start
                          </Button>
                        )}
                        {task.status === 'in_progress' && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            disabled={isUpdatingStatus}
                            onClick={() => handleOpenCompletionDialog(task.id)}
                          >
                            Complete
                          </Button>
                        )}
                        {task.status === 'completed' && (
                          <CheckCircle color="success" fontSize="small" />
                        )}
                        {task.status === 'overdue' && (
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ color: 'warning.main', borderColor: 'warning.main' }}
                            disabled={isUpdatingStatus}
                            onClick={() => handleStartTask(task.id)}
                          >
                            Start
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Alerts & Notifications Tab */}
      {selectedTab === 2 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Maintenance Alerts & Notifications
          </Typography>
          {alerts.length === 0 && !isLoading ? (
            <Alert severity="success">No active maintenance alerts.</Alert>
          ) : (
            <List>
              {(alerts as MaintenanceAlert[]).map((alert) => (
                <Paper key={alert.id} sx={{ mb: 1 }}>
                  <ListItem
                    secondaryAction={
                      !alert.acknowledged && (
                        <Button
                          size="small"
                          onClick={() => handleAcknowledgeAlert(alert.id)}
                        >
                          Acknowledge
                        </Button>
                      )
                    }
                  >
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: getSeverityColor(alert.severity) === 'error' ? 'error.main' : 'warning.main' }}>
                        {alert.severity === 'critical' || alert.severity === 'error' ? <ErrorIcon /> : <Warning />}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body1">
                            {alert.equipmentName}
                          </Typography>
                          <Chip
                            label={alert.severity.toUpperCase()}
                            color={getSeverityColor(alert.severity) as any}
                            size="small"
                          />
                          {alert.acknowledged && (
                            <Chip
                              label="ACKNOWLEDGED"
                              color="success"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="textPrimary">
                            {alert.message}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {alert.timestamp ? new Date(alert.timestamp).toLocaleString() : '--'}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                </Paper>
              ))}
            </List>
          )}
        </Box>
      )}

      {/* Analytics Tab */}
      {selectedTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Paper sx={{ p: 2, height: 400 }}>
              <Typography variant="h6" gutterBottom>
                Equipment Health & Utilization
              </Typography>
              {healthTrendData.length === 0 ? (
                <Alert severity="info">No equipment data available for analytics.</Alert>
              ) : (
                <ResponsiveContainer width="100%" height="90%">
                  <BarChart data={healthTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis domain={[0, 100]} />
                    <Tooltip />
                    <Bar dataKey="health" fill="#4caf50" name="Health Score %" />
                    <Bar dataKey="utilization" fill="#2196f3" name="Utilization %" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: 2, height: 400 }}>
              <Typography variant="h6" gutterBottom>
                Maintenance Type Distribution
              </Typography>
              {maintenanceTypeData.length === 0 ? (
                <Alert severity="info">No task data available for distribution chart.</Alert>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height="70%">
                    <PieChart>
                      <Pie
                        data={maintenanceTypeData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={120}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {maintenanceTypeData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box mt={2}>
                    {maintenanceTypeData.map((item) => (
                      <Box key={item.name} display="flex" justifyContent="space-between" mb={1}>
                        <Box display="flex" alignItems="center">
                          <Box
                            width={12}
                            height={12}
                            bgcolor={item.color}
                            borderRadius="50%"
                            mr={1}
                          />
                          <Typography variant="body2">{item.name}</Typography>
                        </Box>
                        <Typography variant="body2" fontWeight="bold">
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Predictions Tab */}
      {selectedTab === 4 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Failure Predictions
          </Typography>
          {isLoading ? (
            <LinearProgress />
          ) : (predictions as FailurePrediction[]).length === 0 ? (
            <Alert severity="info">No failure predictions available.</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Equipment</TableCell>
                    <TableCell>Failure Probability</TableCell>
                    <TableCell>Predicted Date</TableCell>
                    <TableCell>Recommended Action</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(predictions as FailurePrediction[]).map((pred) => (
                    <TableRow key={pred.id}>
                      <TableCell>{pred.equipmentName}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <LinearProgress
                            variant="determinate"
                            value={pred.failureProbability}
                            color={getPredictionBarColor(pred.failureProbability) as any}
                            sx={{ width: 80, height: 8, borderRadius: 4 }}
                          />
                          <Typography variant="body2">
                            {pred.failureProbability}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {pred.predictedDate ? new Date(pred.predictedDate).toLocaleDateString() : '--'}
                      </TableCell>
                      <TableCell>{pred.recommendedAction || '--'}</TableCell>
                      <TableCell>
                        <Chip label={pred.status || 'active'} size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Completion Dialog */}
      <Dialog open={completionDialogOpen} onClose={() => setCompletionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Complete Maintenance Task</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Completion Notes"
              multiline
              rows={4}
              fullWidth
              required
              value={completionNotes}
              onChange={(e) => setCompletionNotes(e.target.value)}
              error={!!completionNotesError}
              helperText={completionNotesError || 'Minimum 10 characters'}
            />
            <TextField
              label="Actual Duration (minutes)"
              type="number"
              fullWidth
              required
              inputProps={{ min: 1 }}
              value={actualDuration}
              onChange={(e) => setActualDuration(e.target.value === '' ? '' : Number(e.target.value))}
              error={!!actualDurationError}
              helperText={actualDurationError}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompletionDialogOpen(false)} disabled={isUpdatingStatus}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleCompleteTask}
            disabled={isUpdatingStatus}
            startIcon={isUpdatingStatus ? <CircularProgress size={16} /> : undefined}
          >
            {isUpdatingStatus ? 'Completing...' : 'Confirm Complete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Schedule Maintenance Dialog */}
      <Dialog open={scheduleDialogOpen} onClose={() => setScheduleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Schedule Maintenance</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {scheduleError && (
              <Alert severity="error" onClose={() => setScheduleError(null)}>
                {scheduleError}
              </Alert>
            )}

            <FormControl fullWidth required>
              <InputLabel>Equipment</InputLabel>
              <Select
                value={scheduleForm.equipmentId}
                label="Equipment"
                onChange={(e) => handleScheduleFormChange('equipmentId', e.target.value)}
              >
                {(equipment as Equipment[]).map((eq) => (
                  <MenuItem key={eq.id} value={eq.id}>
                    {eq.name} ({eq.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Task Type</InputLabel>
              <Select
                value={scheduleForm.type}
                label="Task Type"
                onChange={(e) => handleScheduleFormChange('type', e.target.value)}
              >
                <MenuItem value="preventive">Preventive</MenuItem>
                <MenuItem value="corrective">Corrective</MenuItem>
                <MenuItem value="predictive">Predictive</MenuItem>
                <MenuItem value="emergency">Emergency</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={scheduleForm.priority}
                label="Priority"
                onChange={(e) => handleScheduleFormChange('priority', e.target.value)}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Scheduled Date"
              type="date"
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              value={scheduleForm.scheduledDate}
              onChange={(e) => handleScheduleFormChange('scheduledDate', e.target.value)}
            />

            <TextField
              label="Estimated Duration (hours)"
              type="number"
              fullWidth
              inputProps={{ min: 0.5, step: 0.5 }}
              value={scheduleForm.estimatedDuration}
              onChange={(e) => handleScheduleFormChange('estimatedDuration', parseFloat(e.target.value) || 1)}
            />

            <TextField
              label="Assigned To"
              fullWidth
              value={scheduleForm.assignedTo}
              onChange={(e) => handleScheduleFormChange('assignedTo', e.target.value)}
            />

            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={scheduleForm.description}
              onChange={(e) => handleScheduleFormChange('description', e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialogOpen(false)} disabled={isCreating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleScheduleSubmit}
            disabled={isCreating}
            startIcon={isCreating ? <CircularProgress size={16} /> : undefined}
          >
            {isCreating ? 'Scheduling...' : 'Schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Equipment Details Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Equipment Details</DialogTitle>
        <DialogContent>
          {selectedEquipment && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Equipment Code</Typography>
                  <Typography variant="body1">{selectedEquipment.code}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Name</Typography>
                  <Typography variant="body1">{selectedEquipment.name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Type</Typography>
                  <Typography variant="body1">{selectedEquipment.type}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Location</Typography>
                  <Typography variant="body1">{selectedEquipment.location}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Health Score</Typography>
                  <Typography variant="body1" color={getHealthColor(selectedEquipment.healthScore)}>
                    {selectedEquipment.healthScore}%
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Utilization Rate</Typography>
                  <Typography variant="body1">{selectedEquipment.utilizationRate}%</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">MTBF</Typography>
                  <Typography variant="body1">{selectedEquipment.mtbf} hours</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">MTTR</Typography>
                  <Typography variant="body1">{selectedEquipment.mttr} hours</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Last Maintenance</Typography>
                  <Typography variant="body1">
                    {selectedEquipment.lastMaintenance
                      ? new Date(selectedEquipment.lastMaintenance).toLocaleString()
                      : '--'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Next Maintenance</Typography>
                  <Typography variant="body1">
                    {selectedEquipment.nextMaintenance
                      ? new Date(selectedEquipment.nextMaintenance).toLocaleString()
                      : '--'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
          <Button variant="contained">Schedule Maintenance</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Maintenance;
